import React, { useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Segmented, Spin, Typography, App } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import { PrintableRouteSheet } from '@/components/manufacturing/PrintableRouteSheet';
import { PrintableRouteSheetOperatorPages } from '@/components/manufacturing/PrintableRouteSheetOperatorPages';
import { PrintableLineInProcessPages } from '@/components/manufacturing/PrintableLineInProcessPages';
import workOrderService from '@/services/workOrder.service';
import { useSystemSettings } from '@/hooks/useSettings';
import type { SystemSetting } from '@/services/settingsService';

const { Text } = Typography;

type RouteSheetView = 'traveler' | 'operator' | 'line-in-process';

function getSetting(settings: SystemSetting[] | undefined, key: string): string {
  if (!settings?.length) return '';
  const s = settings.find((x) => x.setting_key === key);
  return (s?.setting_value ?? '').trim();
}

function parseView(raw: string | null): RouteSheetView {
  return raw === 'operator' || raw === 'line-in-process' ? raw : 'traveler';
}

const WorkOrderRouteSheetPage: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenReason, setRegenReason] = useState('');
  const [generating, setGenerating] = useState(false);

  const view = parseView(searchParams.get('view'));
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['route-sheet-data', workOrderId, baseUrl],
    queryFn: () => workOrderService.getRouteSheetData(workOrderId!, baseUrl),
    enabled: Boolean(workOrderId),
  });

  const { data: settings } = useSystemSettings();

  const company = useMemo(() => {
    const list = settings?.filter((s) => s.category === 'company') ?? [];
    const legal = getSetting(list, 'company_legal_name') || getSetting(list, 'company_name') || 'Company';
    const logo = getSetting(list, 'company_logo') || undefined;
    return { legalName: legal, logoUrl: logo };
  }, [settings]);

  const setView = (next: RouteSheetView) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'traveler') params.delete('view');
    else params.set('view', next);
    setSearchParams(params, { replace: true });
  };

  const handleGenerate = async () => {
    if (!workOrderId) return;
    setGenerating(true);
    try {
      await workOrderService.generateRouteSheet(workOrderId, {
        reason: regenReason.trim() || undefined,
        base_url: baseUrl,
      });
      message.success('Route sheet revision generated');
      setRegenOpen(false);
      setRegenReason('');
      await queryClient.invalidateQueries({ queryKey: ['route-sheet-data', workOrderId] });
      await refetch();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || 'Failed to generate');
    } finally {
      setGenerating(false);
    }
  };

  if (!workOrderId) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Missing work order id</Text>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Could not load route sheet.</Text>
      </div>
    );
  }

  const titlePrefix =
    view === 'operator' ? 'Operator sheets' : view === 'line-in-process' ? 'Line in-process' : 'Route sheet';
  const fileName =
    view === 'operator'
      ? `${data.header.work_order_number}-operator`
      : view === 'line-in-process'
        ? `${data.header.work_order_number}-line-in-process`
        : data.header.work_order_number;

  const extraToolbar = (
    <>
      <Segmented
        className="no-print"
        value={view}
        onChange={(v) => setView(v as RouteSheetView)}
        options={[
          { label: 'Traveler (all ops)', value: 'traveler' },
          { label: 'Operator handoff (1 page / op)', value: 'operator' },
          { label: 'Line in-process (1 page / BOQ line)', value: 'line-in-process' },
        ]}
      />
      {data.header.needs_regeneration && (
        <Button type="primary" onClick={() => setRegenOpen(true)}>
          Regenerate route sheet
        </Button>
      )}
      {!data.header.has_generation ? (
        <Button onClick={() => setRegenOpen(true)}>Generate revision</Button>
      ) : null}
    </>
  );

  return (
    <>
      <PrintPreviewChrome
        title={`${titlePrefix} — ${data.header.work_order_number}`}
        fileName={fileName}
        onBack={() => navigate(`/mfg/work-orders/${workOrderId}`)}
        extraToolbar={extraToolbar}
      >
        {data.header.needs_regeneration && data.header.regeneration_reason && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Route sheet may be outdated"
            description={data.header.regeneration_reason}
          />
        )}
        {view === 'operator' ? (
          <PrintableRouteSheetOperatorPages
            data={data}
            companyLegalName={company.legalName}
            companyLogoUrl={company.logoUrl}
          />
        ) : view === 'line-in-process' ? (
          <PrintableLineInProcessPages data={data} />
        ) : (
          <PrintableRouteSheet
            data={data}
            companyLegalName={company.legalName}
            companyLogoUrl={company.logoUrl}
          />
        )}
      </PrintPreviewChrome>

      <Modal
        title="Regenerate route sheet"
        open={regenOpen}
        onCancel={() => setRegenOpen(false)}
        onOk={handleGenerate}
        confirmLoading={generating}
        okText="Generate new revision"
      >
        <p style={{ marginBottom: 12 }}>
          This creates a new revision without overwriting prior generations. Use after work order changes.
        </p>
        <Input.TextArea
          rows={3}
          placeholder="Reason for revision (optional)"
          value={regenReason}
          onChange={(e) => setRegenReason(e.target.value)}
        />
      </Modal>
    </>
  );
};

export default WorkOrderRouteSheetPage;
